import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CRMProvider } from './context/CRMContext';
import { CRMDataProvider } from './context/CRMDataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import TenantProvider, { TenantGate, useTenant } from './context/TenantContext';
import { TeamProvider } from './context/TeamContext';
import { SettingsProvider } from './context/SettingsContext';
import { OutreachProvider } from './context/OutreachContext';
import { LinkedInProvider } from './context/LinkedInContext';
import { BrregProvider } from './context/BrregContext';
import { EmailProvider } from './context/EmailContext';
import { SequenceProvider } from './context/SequenceContext';
import { PlaybookProvider } from './context/PlaybookContext';
import { LeadIntelProvider } from './context/LeadIntelContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { hexToRgb } from './utils/helpers';
import { getRolePermissions } from './utils/rolePermissions';

import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Customers from './pages/Customers';
import Clients from './pages/Clients';
import CompanyDetails from './pages/CompanyDetails';
import Contacts from './pages/Contacts';
import ContactDetails from './pages/ContactDetails';
import Pipeline from './pages/Pipeline';
import DealForecast from './pages/DealForecast';
import ProjectList from './pages/ProjectList';
import ProjectCanvas from './pages/ProjectCanvas';
import Activities from './pages/Activities';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import SocialMedia from './pages/SocialMedia';
import LandingPage from './pages/LandingPage';
import SellerDashboard from './pages/SellerDashboard';
import CustomerRooms from './pages/CustomerRooms';
import CustomerPortal from './pages/CustomerPortal';
import ContractTemplates from './pages/ContractTemplates';
import SentContracts from './pages/SentContracts';
import Products from './pages/Products';
import Integrations from './pages/Integrations';
import EmailTemplates from './pages/EmailTemplates';
import EmailSequences from './pages/EmailSequences';
import SequenceBuilder from './pages/SequenceBuilder';
import SequenceImporter from './pages/SequenceImporter';
import TemplateImporter from './pages/TemplateImporter';
import MassEmailHistory from './pages/MassEmailHistory';
import KnowledgeBase from './pages/KnowledgeBase';
import LeadImportCenter from './pages/LeadImportCenter';
import OutreachDashboard from './pages/OutreachDashboard';
import EmailCopilot from './pages/EmailCopilot';
import BrregSettings from './pages/BrregSettings';
import PlaybookMarketplace from './pages/PlaybookMarketplace';
import Governance from './pages/Governance';
import LeadIntelligence from './pages/LeadIntelligence';
import ComplianceDashboard from './pages/ComplianceDashboard';
import ComplianceBoardView from './pages/ComplianceBoardView';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AICoach from './pages/AICoach';
import SuperAdmin from './pages/SuperAdmin';
import Billing from './pages/Billing';
import Support from './pages/Support';
import Scoreboard from './pages/Scoreboard';
import EmployeeSignup from './pages/EmployeeSignup';
import TenantAdminView from './components/superadmin/TenantAdminView';

import { Terms, Privacy, Cookies } from './pages/legal/LegalPages';
import { PublicMicrosite, RedirectHandler } from './components/outreach/PublicRoutes';
import PartnerPortal from './pages/PartnerPortal';
import SellerPortal from './pages/SellerPortal';
import Careers from './pages/Careers';
import Features from './pages/Features';
import Signup from './pages/Signup';
import BookDemo from './pages/BookDemo';
import FAQ from './pages/FAQ';
import PartnerRegister from './pages/PartnerRegister';

const isProduction = import.meta.env.PROD === true;

const ThemeApplicator = () => {
  const { themeSettings } = useAuth();

  useEffect(() => {
    if (themeSettings) {
      const root = document.documentElement;

      if (themeSettings.primaryColor) {
        root.style.setProperty('--color-primary', hexToRgb(themeSettings.primaryColor));
        root.style.setProperty('--color-primary-dark', hexToRgb(themeSettings.primaryColor));
      }

      if (themeSettings.sidebarColor) {
        root.style.setProperty('--color-sidebar-bg', hexToRgb(themeSettings.sidebarColor));
        root.style.setProperty('--color-sidebar-hover', hexToRgb(themeSettings.sidebarColor));
      }
    }
  }, [themeSettings]);

  return null;
};

const TenantProtected = ({ children }) => (
  <TenantGate>
    <BrregProvider>
      <TeamProvider>
        <CRMDataProvider>{children}</CRMDataProvider>
      </TeamProvider>
    </BrregProvider>
  </TenantGate>
);

const ProtectedRoute = ({ children, superAdminOnly = false }) => {
  const { isAuthenticated, currentUser, loading, previewMode } = useAuth();

  if (loading) return null;

  const hasPreviewBypass =
    previewMode ||
    currentUser?.isDemoUser ||
    currentUser?.isPreviewUser;

  if (!isProduction && hasPreviewBypass) {
    if (superAdminOnly && !currentUser?.isSuperAdmin) {
      return <Navigate to="/dashboard" replace />;
    }

    return <TenantProtected>{children}</TenantProtected>;
  }

  if (!isAuthenticated || (isProduction && hasPreviewBypass)) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && !currentUser?.isSuperAdmin) {
    if (!isProduction) {
      console.warn('[SUPERADMIN ACCESS DENIED]', {
        email: currentUser?.email,
        role: currentUser?.role,
        appMetadataRole: currentUser?.appMetadataRole,
        isSuperAdmin: currentUser?.isSuperAdmin
      });
    }

    return <Navigate to="/dashboard" replace />;
  }

  return <TenantProtected>{children}</TenantProtected>;
};

const PermissionRoute = ({ children, permission }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { memberships, activeTenantId, activeTenant } = useTenant();
  const activeMembership = memberships.find(
    membership => membership.tenant_id === activeTenantId
  );
  const permissions = getRolePermissions(currentUser, activeMembership, activeTenant);

  if (authLoading) {
    return null;
  }

  if (!permissions[permission]) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => (
  <PermissionRoute permission="canSettings">{children}</PermissionRoute>
);

const AppRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    if (!isProduction) {
      console.log('Current route', window.location.hash);
    }
  }, [location]);

  return (
    <>
      <ThemeApplicator />
      <Routes>
        <Route path="/" element={<LandingPage />} />

        <Route path="/careers" element={<Careers />} />
        <Route path="/features" element={<Features />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/book-demo" element={<BookDemo />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/partner-register" element={<PartnerRegister />} />

        <Route path="/ms/:slug" element={<PublicMicrosite />} />
        <Route path="/go/:code" element={<RedirectHandler />} />
        <Route path="/portal/:roomId" element={<CustomerPortal />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/employee-signup" element={<EmployeeSignup />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />

        <Route
          path="/super-admin/tenants"
          element={
            <ProtectedRoute superAdminOnly>
              <TenantAdminView onBack={() => window.history.back()} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/super-admin"
          element={
            <ProtectedRoute superAdminOnly>
              <SuperAdmin />
            </ProtectedRoute>
          }
        />

        <Route path="/partner-portal" element={<ProtectedRoute><PartnerPortal /></ProtectedRoute>} />
        <Route path="/seller-portal" element={<ProtectedRoute><SellerPortal /></ProtectedRoute>} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/support" element={<Support />} />

          <Route path="/dashboard/compliance" element={<AdminRoute><ComplianceDashboard /></AdminRoute>} />
          <Route path="/dashboard/board-compliance" element={<AdminRoute><ComplianceBoardView /></AdminRoute>} />

          <Route path="/seller" element={<SellerDashboard />} />
          <Route path="/scoreboard" element={<Scoreboard />} />

          <Route path="/companies" element={<Companies />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/companies/:id" element={<CompanyDetails />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/contacts/:id" element={<ContactDetails />} />
          <Route path="/pipeline" element={<PermissionRoute permission="canViewPipeline"><Pipeline /></PermissionRoute>} />
          <Route path="/forecast" element={<DealForecast />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/:projectId" element={<ProjectCanvas />} />
          <Route path="/contract-templates" element={<AdminRoute><ContractTemplates /></AdminRoute>} />
          <Route path="/contracts" element={<SentContracts />} />
          <Route path="/products" element={<AdminRoute><Products /></AdminRoute>} />
          <Route path="/customer-rooms" element={<CustomerRooms />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/reports" element={<PermissionRoute permission="canViewReports"><Reports /></PermissionRoute>} />
          <Route path="/social" element={<AdminRoute><SocialMedia /></AdminRoute>} />
          <Route path="/email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
          <Route path="/email-templates/import" element={<AdminRoute><TemplateImporter /></AdminRoute>} />
          <Route path="/sequences" element={<AdminRoute><EmailSequences /></AdminRoute>} />
          <Route path="/sequences/import" element={<AdminRoute><SequenceImporter /></AdminRoute>} />
          <Route path="/sequences/:id/edit" element={<AdminRoute><SequenceBuilder /></AdminRoute>} />
          <Route path="/mass-email" element={<AdminRoute><MassEmailHistory /></AdminRoute>} />
          <Route path="/integrations" element={<AdminRoute><Integrations /></AdminRoute>} />
          <Route path="/knowledge-base" element={<PermissionRoute permission="canViewKnowledgeBase"><KnowledgeBase /></PermissionRoute>} />
          <Route path="/lead-import" element={<PermissionRoute permission="canImportBrreg"><LeadImportCenter /></PermissionRoute>} />
          <Route path="/outreach" element={<PermissionRoute permission="canOutreach"><OutreachDashboard /></PermissionRoute>} />
          <Route path="/email-copilot" element={<AdminRoute><EmailCopilot /></AdminRoute>} />
          <Route path="/settings/brreg" element={<PermissionRoute permission="canImportBrreg"><BrregSettings /></PermissionRoute>} />
          <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
          <Route path="/marketplace" element={<AdminRoute><PlaybookMarketplace /></AdminRoute>} />
          <Route path="/governance" element={<AdminRoute><Governance /></AdminRoute>} />
          <Route path="/lead-intelligence" element={<PermissionRoute permission="canOutreach"><LeadIntelligence /></PermissionRoute>} />
          <Route path="/ai-coach" element={<PermissionRoute permission="canOutreach"><AICoach /></PermissionRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App = () => (
  <HashRouter>
    <AuthProvider>
      <TenantProvider>
        <SettingsProvider>
          <CRMProvider>
            <SubscriptionProvider>
              <LinkedInProvider>
                <OutreachProvider>
                  <EmailProvider>
                    <SequenceProvider>
                      <PlaybookProvider>
                        <LeadIntelProvider>
                          <AppRoutes />
                        </LeadIntelProvider>
                      </PlaybookProvider>
                    </SequenceProvider>
                  </EmailProvider>
                </OutreachProvider>
              </LinkedInProvider>
            </SubscriptionProvider>
          </CRMProvider>
        </SettingsProvider>
      </TenantProvider>
    </AuthProvider>
  </HashRouter>
);

export default App;